import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchProjectFinancialOverview, 
  fetchProjectTransactions, 
  fetchProjectLaborCosts, 
  fetchOpenBalanceInvoices,
  setActiveFinancialTab,
  disconnectQuickbooks
} from '../../../store/slices/financialSlice';
import Spinner from '../../common/Spinner';
import { showToast } from '../../utils/Toast';
import Modal from '../../common/Modal';
import { config } from '../../../config/config';
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import { Doughnut, Bar, Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

// Status Badge Component - matches app pattern
const StatusBadge = ({ status, type }) => {
  const getStatusClasses = () => {
    const baseClasses = "px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full";
    const statusLower = status?.toLowerCase();
    const typeLower = type?.toLowerCase();
    
    // Type-based colors
    if (typeLower === 'estimate') return `${baseClasses} bg-purple-100 text-purple-800`;
    if (typeLower === 'invoice') {
      if (statusLower === 'paid') return `${baseClasses} bg-green-100 text-green-800`;
      if (statusLower === 'overdue') return `${baseClasses} bg-red-100 text-red-800`;
      return `${baseClasses} bg-blue-100 text-blue-800`;
    }
    if (typeLower === 'payment') return `${baseClasses} bg-green-100 text-green-800`;
    if (typeLower === 'deposit') return `${baseClasses} bg-teal-100 text-teal-800`;
    if (typeLower === 'expense' || typeLower === 'purchase') return `${baseClasses} bg-red-100 text-red-800`;
    if (typeLower === 'bill') return `${baseClasses} bg-yellow-100 text-yellow-800`;
    
    // Status-based colors (fallback)
    switch (statusLower) {
      case 'paid':
      case 'completed':
      case 'accepted':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'open':
      case 'pending':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'overdue':
      case 'rejected':
        return `${baseClasses} bg-red-100 text-red-800`;
      case 'draft':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };
  
  // Display text: prefer status for invoices, otherwise use type
  let displayText = status || type || 'Unknown';
  displayText = displayText.charAt(0).toUpperCase() + displayText.slice(1);
  
  return <span className={getStatusClasses()}>{displayText}</span>;
};

// Transaction Type Badge Component
const TypeBadge = ({ type }) => {
  const baseClasses = "px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full";
  
  const getTypeClasses = () => {
    switch (type?.toLowerCase()) {
      case 'estimate':
        return `${baseClasses} bg-purple-100 text-purple-800`;
      case 'invoice':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'payment':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'deposit':
        return `${baseClasses} bg-teal-100 text-teal-800`;
      case 'expense':
      case 'purchase':
        return `${baseClasses} bg-red-100 text-red-800`;
      case 'bill':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };
  
  const displayText = type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Other';
  
  return <span className={getTypeClasses()}>{displayText}</span>;
};

// Empty State Component
const EmptyState = ({ message }) => (
  <div className="text-center py-8 text-gray-500">
    {message}
  </div>
);

const FinancialTab = ({ project }) => {
  const dispatch = useDispatch();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authUrl, setAuthUrl] = useState('');
  
  // Labor filtering and sorting states
  const [laborDateFilter, setLaborDateFilter] = useState('all');
  const [laborEmployeeFilter, setLaborEmployeeFilter] = useState('all');
  const [laborBillableFilter, setLaborBillableFilter] = useState('all');
  const [laborSortBy, setLaborSortBy] = useState('date');
  const [laborSortOrder, setLaborSortOrder] = useState('desc');
  const [laborGroupBy, setLaborGroupBy] = useState('none');
  
  const { 
    financialOverview, 
    transactions, 
    laborCosts, 
    openBalanceInvoices, 
    allInvoices,
    estimates, 
    loading, 
    disconnecting,
    error, 
    activeFinancialTab,
    authRequired
  } = useSelector(state => state.financial);

  const currentUser = useSelector(state => state.auth?.user);
  const isAdmin = currentUser?.role === 'super_admin' || currentUser?.role === 'admin';

  useEffect(() => {
    if (authRequired && error === 'QuickBooks authentication required') {
      fetch(`${config.apiUrl}/api/quickbooks/auth-url`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
        .then(response => response.json())
        .then(data => {
          if (data.success && data.authUrl) {
            setAuthUrl(data.authUrl);
            setShowAuthModal(true);
          }
        })
        .catch(() => showToast('Failed to get QuickBooks authentication URL', 'error'));
    }
  }, [authRequired, error]);

  useEffect(() => {
    if (project?._id) {
      dispatch(fetchProjectFinancialOverview(project._id)).unwrap().catch(error => {
        const errorMessage = typeof error === 'string' ? error : error?.message || 'An error occurred';
        const isAuthError = error?.authRequired || errorMessage.includes('QuickBooks authentication required');
        if (!isAuthError) showToast(errorMessage, 'error');
        });
      
      if (activeFinancialTab === 'transactions') {
        dispatch(fetchProjectTransactions(project._id));
      } else if (activeFinancialTab === 'labor') {
        dispatch(fetchProjectLaborCosts(project._id));
      } else if (activeFinancialTab === 'invoices') {
        dispatch(fetchOpenBalanceInvoices(project._id));
      }
    }
  }, [project?._id, activeFinancialTab, dispatch]);

  const handleTabChange = (tab) => dispatch(setActiveFinancialTab(tab));

  if (loading && !financialOverview) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="xl" color="blue" />
      </div>
    );
  }

  const handleAuthenticate = async () => {
    try {
      let urlToUse = authUrl;
      if (!urlToUse) {
        const response = await fetch(`${config.apiUrl}/api/quickbooks/auth-url`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        const data = await response.json();
        if (data.success && data.authUrl) {
          urlToUse = data.authUrl;
          setAuthUrl(data.authUrl);
        } else {
          showToast('Failed to get QuickBooks authentication URL', 'error');
          return;
        }
      }
      
      const authWindow = window.open(urlToUse, 'quickbooksAuth', 'width=600,height=700');
      if (!authWindow || authWindow.closed) {
        showToast('Popup blocked! Please allow popups for this site.', 'error');
        return;
    }
    
      setShowAuthModal(false);
      
    const checkInterval = setInterval(() => {
      try {
        if (authWindow.closed) {
          clearInterval(checkInterval);
          setTimeout(() => {
            if (project?._id) {
                dispatch(fetchProjectFinancialOverview(project._id)).unwrap()
                  .then(() => showToast('QuickBooks connected!', 'success'))
                  .catch(error => { if (!error?.authRequired) showToast(error?.message || 'Failed to load data', 'error'); });
            }
            }, 1500);
        }
        } catch (e) { clearInterval(checkInterval); }
    }, 1000);
    } catch (error) {
      showToast('Failed to initiate QuickBooks authentication', 'error');
    }
  };

  const handleDisconnect = async () => {
    if (window.confirm('Disconnect from QuickBooks?')) {
      try {
        await dispatch(disconnectQuickbooks()).unwrap();
        showToast('Disconnected successfully', 'success');
      } catch (error) {
        showToast(error?.message || 'Failed to disconnect', 'error');
      }
    }
  };

  if (error && error !== 'QuickBooks authentication required') {
    return (
      <div className="p-4 border border-red-300 bg-red-50 rounded-md text-red-800">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-semibold mb-2">Error Loading Financial Data</h3>
            <p>{error}</p>
            {isAdmin && (
              <p className="text-sm text-red-700 mt-2">
                If this looks like a QuickBooks token issue, you can reconnect below.
              </p>
            )}
          </div>
          {isAdmin && (
            <button
              onClick={handleAuthenticate}
              className="shrink-0 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              Reconnect to QuickBooks
            </button>
          )}
        </div>
      </div>
    );
  }

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Calculate labor totals
  const totalHours = laborCosts?.reduce((sum, l) => sum + parseFloat(l.hours || 0), 0) || 0;
  const totalLaborAmount = laborCosts?.reduce((sum, l) => sum + parseFloat(l.amount || 0), 0) || 0;

  // Filter and sort labor costs
  const getFilteredAndSortedLaborCosts = () => {
    if (!laborCosts || laborCosts.length === 0) return [];
    
    let filtered = [...laborCosts];
    
    // Date filter
    if (laborDateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(labor => {
        const laborDate = new Date(labor.date);
        switch (laborDateFilter) {
          case 'today':
            return laborDate >= today;
          case 'week':
            const weekAgo = new Date(today);
            weekAgo.setDate(weekAgo.getDate() - 7);
            return laborDate >= weekAgo;
          case 'month':
            const monthAgo = new Date(today);
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            return laborDate >= monthAgo;
          default:
            return true;
        }
      });
    }
    
    // Employee filter
    if (laborEmployeeFilter !== 'all') {
      filtered = filtered.filter(labor => labor.employeeName === laborEmployeeFilter);
    }
    
    // Billable filter
    if (laborBillableFilter !== 'all') {
      filtered = filtered.filter(labor => 
        laborBillableFilter === 'billable' ? labor.isBillable : !labor.isBillable
      );
    }
    
    // Sort
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (laborSortBy) {
        case 'date':
          comparison = new Date(a.date) - new Date(b.date);
          break;
        case 'employee':
          comparison = (a.employeeName || '').localeCompare(b.employeeName || '');
          break;
        case 'hours':
          comparison = parseFloat(a.hours || 0) - parseFloat(b.hours || 0);
          break;
        case 'amount':
          comparison = parseFloat(a.amount || 0) - parseFloat(b.amount || 0);
          break;
        default:
          comparison = 0;
      }
      return laborSortOrder === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  };

  const filteredLaborCosts = getFilteredAndSortedLaborCosts();
  
  // Get unique employees for filter
  const uniqueEmployees = [...new Set(laborCosts?.map(l => l.employeeName).filter(Boolean))] || [];
  
  // Group labor costs if needed
  const getGroupedLaborCosts = () => {
    if (laborGroupBy === 'none') return { 'All Entries': filteredLaborCosts };
    
    const grouped = {};
    filteredLaborCosts.forEach(labor => {
      let key;
      if (laborGroupBy === 'employee') {
        key = labor.employeeName || 'Unknown';
      } else if (laborGroupBy === 'date') {
        key = labor.date ? new Date(labor.date).toLocaleDateString() : 'Unknown Date';
      }
      
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(labor);
    });
    
    return grouped;
  };
  
  const groupedLaborCosts = getGroupedLaborCosts();
  
  // Calculate filtered totals
  const filteredTotalHours = filteredLaborCosts.reduce((sum, l) => sum + parseFloat(l.hours || 0), 0);
  const filteredTotalAmount = filteredLaborCosts.reduce((sum, l) => sum + parseFloat(l.amount || 0), 0);
  const filteredBillableHours = filteredLaborCosts.filter(l => l.isBillable).reduce((sum, l) => sum + parseFloat(l.hours || 0), 0);
  const filteredBillableAmount = filteredLaborCosts.filter(l => l.isBillable).reduce((sum, l) => sum + parseFloat(l.amount || 0), 0);

  return (
    <div className="space-y-6">
      {/* QuickBooks Connected Banner */}
      {!authRequired && isAdmin && (
        <div className="p-4 border border-green-300 bg-green-50 rounded-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="font-semibold text-green-900">Connected to QuickBooks</h3>
                <p className="text-green-700 text-sm">Financial data is being synced from QuickBooks.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleAuthenticate}
                className="px-4 py-2 text-blue-700 hover:text-blue-800 hover:bg-blue-50 border border-blue-300 rounded-md text-sm font-medium"
                title="Re-authorize QuickBooks if your tokens have expired or were revoked"
              >
                Reconnect
              </button>
              <button
                onClick={handleDisconnect}
                disabled={disconnecting}
                className="px-4 py-2 text-red-600 hover:text-red-700 text-sm font-medium"
              >
                {disconnecting ? 'Disconnecting...' : 'Disconnect'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QuickBooks Authentication Banner */}
      {authRequired && (
        <div className="p-4 border border-blue-300 bg-blue-50 rounded-md">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">QuickBooks Authentication Required</h3>
              <p className="text-blue-800 text-sm">
                {isAdmin 
                  ? 'To access financial data, you need to connect to QuickBooks.'
                  : 'QuickBooks is not connected. Please contact an administrator.'}
              </p>
            </div>
            {isAdmin && (
              <button
                onClick={handleAuthenticate}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
              >
                Connect to QuickBooks
              </button>
            )}
          </div>
        </div>
      )}

      {showAuthModal && (
        <Modal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} title="Connect to QuickBooks">
          <div className="p-4">
            <p className="text-sm text-gray-600 mb-4">Click below to authenticate with QuickBooks.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowAuthModal(false)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md border border-gray-300">Cancel</button>
              <button onClick={handleAuthenticate} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">Connect</button>
            </div>
          </div>
        </Modal>
      )}
      
      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {['overview', 'invoices', 'transactions', 'labor'].map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${
                activeFinancialTab === tab
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="pt-2">
        {/* OVERVIEW TAB */}
        {activeFinancialTab === 'overview' && (
          <div className="space-y-6">
            {financialOverview ? (
              <>
                {/* Key Metrics Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white p-6 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-gray-500">Total Revenue</p>
                      <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{formatCurrency(financialOverview.totalInvoiced)}</p>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-sm text-gray-600">{financialOverview.invoiceCount || 0} invoices</p>
                      <p className="text-xs text-gray-500">{financialOverview.estimateCount || 0} estimates</p>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-gray-500">Collected</p>
                      <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-3xl font-bold text-green-600">{formatCurrency(financialOverview.totalPaid)}</p>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-sm text-gray-600">
                        {financialOverview.totalInvoiced > 0 
                          ? `${((financialOverview.totalPaid / financialOverview.totalInvoiced) * 100).toFixed(1)}%`
                          : '0%'} collected
                      </p>
                      <p className="text-xs text-gray-500">{transactions?.length || 0} transactions</p>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-gray-500">Outstanding</p>
                      <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-3xl font-bold text-yellow-600">{formatCurrency(financialOverview.totalBalance)}</p>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-sm text-gray-600">{financialOverview.openInvoices?.count || 0} open</p>
                      <p className="text-xs text-red-500">{financialOverview.overdueInvoices?.count || 0} overdue</p>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-gray-500">Net Profit</p>
                      <svg className="w-8 h-8 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                    </div>
                    <p className={`text-3xl font-bold ${(financialOverview.netProfit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(financialOverview.netProfit)}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-sm text-gray-600">{(financialOverview.profitMargin || 0).toFixed(1)}% margin</p>
                      <p className="text-xs text-gray-500">Costs: {formatCurrency(financialOverview.totalExpenses || 0)}</p>
                    </div>
                  </div>
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Revenue Breakdown - Doughnut Chart */}
                  <div className="bg-white p-6 rounded-lg border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Breakdown</h3>
                    <div className="h-64 flex items-center justify-center">
                      <Doughnut
                        data={{
                          labels: ['Collected', 'Outstanding', 'Overdue'],
                          datasets: [{
                            data: [
                              financialOverview.totalPaid || 0,
                              (financialOverview.totalBalance || 0) - (financialOverview.overdueInvoices?.total || 0),
                              financialOverview.overdueInvoices?.total || 0
                            ],
                            backgroundColor: ['#10b981', '#f59e0b', '#ef4444'],
                            borderColor: ['#fff', '#fff', '#fff'],
                            borderWidth: 2
                          }]
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { position: 'bottom', labels: { padding: 15, font: { size: 12 } } },
                            tooltip: {
                              callbacks: {
                                label: (context) => {
                                  const label = context.label || '';
                                  const value = context.parsed || 0;
                                  return `${label}: ${formatCurrency(value)}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* Cost Breakdown - Doughnut Chart */}
                  <div className="bg-white p-6 rounded-lg border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Profit Breakdown</h3>
                    <div className="h-64 flex items-center justify-center">
                      <Doughnut
                        data={{
                          labels: ['Cost of Goods Sold', 'Payroll/Labor', 'Net Profit'],
                          datasets: [{
                            data: [
                              financialOverview.totalCostOfGoodsSold || 0,
                              financialOverview.totalLaborCost || 0,
                              Math.max(0, financialOverview.netProfit || 0)
                            ],
                            backgroundColor: ['#f97316', '#3b82f6', '#10b981'],
                            borderColor: ['#fff', '#fff', '#fff'],
                            borderWidth: 2
                          }]
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { position: 'bottom', labels: { padding: 15, font: { size: 12 } } },
                            tooltip: {
                              callbacks: {
                                label: (context) => {
                                  const label = context.label || '';
                                  const value = context.parsed || 0;
                                  const total = financialOverview.totalInvoiced || 1;
                                  const percentage = ((value / total) * 100).toFixed(1);
                                  return `${label}: ${formatCurrency(value)} (${percentage}%)`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>


                {/* Recent Transactions */}
                <div className="bg-white rounded-lg border border-gray-200">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium">Recent Transactions</h3>
                  </div>
                  {financialOverview.recentTransactions && financialOverview.recentTransactions.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {financialOverview.recentTransactions.map((txn, idx) => (
                            <tr key={txn.id || idx}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {txn.date ? new Date(txn.date).toLocaleDateString() : '-'}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">{txn.description || '-'}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <TypeBadge type={txn.type} />
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <span className={
                                  txn.type === 'payment' || txn.type === 'deposit' 
                                    ? 'text-green-600' 
                                    : txn.type === 'expense' || txn.type === 'bill' 
                                      ? 'text-red-600' 
                                      : 'text-gray-900'
                                }>
                                  {formatCurrency(txn.amount)}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <EmptyState message="No recent transactions" />
                  )}
                </div>
              </>
            ) : (
              <EmptyState message="No financial data available. This job may not exist in QuickBooks." />
            )}
          </div>
        )}
        
        {/* INVOICES TAB */}
        {activeFinancialTab === 'invoices' && (
          <div className="space-y-6">
            {/* All Invoices */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium">Invoices</h3>
              </div>
              {(allInvoices && allInvoices.length > 0) || (openBalanceInvoices && openBalanceInvoices.length > 0) ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Number</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paid</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                      {(allInvoices || openBalanceInvoices || []).map((invoice, index) => (
                        <tr key={invoice.id || index} className={invoice.status === 'overdue' ? 'bg-red-50' : 'hover:bg-gray-50'}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {invoice.date ? new Date(invoice.date).toLocaleDateString() : '-'}
                        </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">{invoice.number || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{invoice.customer || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : '-'}
                        </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(invoice.amount)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                            {formatCurrency(invoice.paidAmount || (invoice.amount - invoice.openBalance))}
                        </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">{formatCurrency(invoice.openBalance)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <StatusBadge status={invoice.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
                <EmptyState message="No invoices found" />
              )}
            </div>

            {/* Estimates */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium">Estimates</h3>
              </div>
              {estimates && estimates.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Number</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiration</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {estimates.map((estimate, index) => (
                        <tr key={estimate.id || index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {estimate.date ? new Date(estimate.date).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">{estimate.number || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{estimate.customer || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {estimate.expirationDate ? new Date(estimate.expirationDate).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">{formatCurrency(estimate.amount)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <StatusBadge status={estimate.status} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState message="No estimates found" />
              )}
            </div>
          </div>
        )}
        
        {/* TRANSACTIONS TAB */}
        {activeFinancialTab === 'transactions' && (
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium">Transactions</h3>
              <p className="text-sm text-gray-500 mt-1">Estimates, invoices, payments, deposits, and expenses</p>
            </div>
            {transactions && transactions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Doc #</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer/Vendor</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {transactions.map((transaction, index) => (
                      <tr key={transaction.id || index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {transaction.date ? new Date(transaction.date).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                          {transaction.docNumber || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate" title={transaction.description}>
                          {transaction.description || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <TypeBadge type={transaction.type} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {transaction.status ? (
                            <StatusBadge status={transaction.status} type={transaction.type} />
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{transaction.fromTo || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                          <span className={
                            transaction.type === 'payment' || transaction.type === 'deposit' 
                              ? 'text-green-600' 
                              : transaction.type === 'expense' || transaction.type === 'bill' 
                                ? 'text-red-600' 
                                : ''
                          }>
                            {formatCurrency(transaction.amount)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState message="No transactions found" />
            )}
          </div>
        )}
        
        {/* LABOR TAB */}
        {activeFinancialTab === 'labor' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <p className="text-sm font-medium text-gray-500 mb-1">Total Entries</p>
                <p className="text-2xl font-bold text-gray-900">{filteredLaborCosts.length}</p>
                {filteredLaborCosts.length !== laborCosts?.length && (
                  <p className="text-xs text-gray-400 mt-1">of {laborCosts?.length || 0} total</p>
                )}
              </div>
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <p className="text-sm font-medium text-gray-500 mb-1">Total Hours</p>
                <p className="text-2xl font-bold text-gray-900">{filteredTotalHours.toFixed(2)}</p>
                <p className="text-xs text-gray-400 mt-1">hrs worked</p>
              </div>
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <p className="text-sm font-medium text-gray-500 mb-1">Total Cost</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(filteredTotalAmount)}</p>
              </div>
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <p className="text-sm font-medium text-gray-500 mb-1">Billable</p>
                <p className="text-2xl font-bold text-green-600">{filteredBillableHours.toFixed(2)}</p>
                <p className="text-xs text-gray-400 mt-1">{formatCurrency(filteredBillableAmount)}</p>
              </div>
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <p className="text-sm font-medium text-gray-500 mb-1">Non-Billable</p>
                <p className="text-2xl font-bold text-gray-600">{(filteredTotalHours - filteredBillableHours).toFixed(2)}</p>
                <p className="text-xs text-gray-400 mt-1">{formatCurrency(filteredTotalAmount - filteredBillableAmount)}</p>
              </div>
            </div>

            {/* Filters and Controls */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                {/* Date Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                  <select 
                    value={laborDateFilter} 
                    onChange={(e) => setLaborDateFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">Last 7 Days</option>
                    <option value="month">Last 30 Days</option>
                  </select>
                </div>

                {/* Employee Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
                  <select 
                    value={laborEmployeeFilter} 
                    onChange={(e) => setLaborEmployeeFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="all">All Employees</option>
                    {uniqueEmployees.map(emp => (
                      <option key={emp} value={emp}>{emp}</option>
                    ))}
                  </select>
                </div>

                {/* Billable Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Billable Status</label>
                  <select 
                    value={laborBillableFilter} 
                    onChange={(e) => setLaborBillableFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="all">All</option>
                    <option value="billable">Billable Only</option>
                    <option value="non-billable">Non-Billable Only</option>
                  </select>
                </div>

                {/* Sort By */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                  <select 
                    value={laborSortBy} 
                    onChange={(e) => setLaborSortBy(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="date">Date</option>
                    <option value="employee">Employee</option>
                    <option value="hours">Hours</option>
                    <option value="amount">Amount</option>
                  </select>
                </div>

                {/* Sort Order */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
                  <select 
                    value={laborSortOrder} 
                    onChange={(e) => setLaborSortOrder(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="desc">Descending</option>
                    <option value="asc">Ascending</option>
                  </select>
                </div>

                {/* Group By */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Group By</label>
                  <select 
                    value={laborGroupBy} 
                    onChange={(e) => setLaborGroupBy(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="none">No Grouping</option>
                    <option value="employee">Employee</option>
                    <option value="date">Date</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Labor Entries Table */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-medium">Employee Time Entries</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {filteredLaborCosts.length} {filteredLaborCosts.length === 1 ? 'entry' : 'entries'} • 
                      {laborGroupBy !== 'none' && ` Grouped by ${laborGroupBy} •`}
                      Time tracked from QuickBooks
                    </p>
                  </div>
                </div>
              </div>
              {filteredLaborCosts && filteredLaborCosts.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clock In</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clock Out</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hours</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Billable</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                      {Object.entries(groupedLaborCosts).map(([groupName, groupEntries]) => (
                        <React.Fragment key={groupName}>
                          {laborGroupBy !== 'none' && (
                            <tr className="bg-gray-100">
                              <td colSpan="9" className="px-6 py-3">
                                <div className="flex justify-between items-center">
                                  <span className="font-semibold text-gray-900">{groupName}</span>
                                  <span className="text-sm text-gray-600">
                                    {groupEntries.length} {groupEntries.length === 1 ? 'entry' : 'entries'} • 
                                    {groupEntries.reduce((sum, l) => sum + parseFloat(l.hours || 0), 0).toFixed(2)} hrs • 
                                    {formatCurrency(groupEntries.reduce((sum, l) => sum + parseFloat(l.amount || 0), 0))}
                                  </span>
                                </div>
                              </td>
                            </tr>
                          )}
                          {groupEntries.map((labor, index) => {
                        const formatTime = (timeString) => {
                          if (!timeString) return '-';
                          try {
                            const date = new Date(timeString);
                            return date.toLocaleTimeString('en-US', { 
                              hour: 'numeric', 
                              minute: '2-digit',
                              hour12: true 
                            });
                          } catch {
                            return '-';
                          }
                        };

                        return (
                          <tr key={labor.id || labor._id || index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {labor.date ? new Date(labor.date).toLocaleDateString() : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">{labor.employeeName || '-'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {labor.startTime ? (
                                <span className="inline-flex items-center">
                                  <svg className="w-4 h-4 mr-1 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                  </svg>
                                  {formatTime(labor.startTime)}
                                </span>
                              ) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {labor.endTime ? (
                                <span className="inline-flex items-center">
                                  <svg className="w-4 h-4 mr-1 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                  </svg>
                                  {formatTime(labor.endTime)}
                                </span>
                              ) : '-'}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={labor.description}>
                              {labor.description || '-'}
                        </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">{(labor.hours || 0).toFixed(2)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${(labor.rate || 0).toFixed(2)}/hr</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              {labor.isBillable ? (
                                <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                  Billable
                                </span>
                              ) : (
                                <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                  Non-Billable
                                </span>
                              )}
                        </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">{formatCurrency(labor.amount)}</td>
                          </tr>
                        );
                      })}
                        </React.Fragment>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td colSpan="5" className="px-6 py-4 text-sm font-medium text-gray-900">
                          {laborGroupBy !== 'none' ? 'Grand Total' : 'Total'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{filteredTotalHours.toFixed(2)} hrs</td>
                        <td className="px-6 py-4"></td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className="text-xs font-medium text-green-600">{filteredBillableHours.toFixed(2)} billable</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{formatCurrency(filteredTotalAmount)}</td>
                      </tr>
                    </tfoot>
                </table>
              </div>
            ) : (
                <EmptyState message="No employee time entries found for this project" />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FinancialTab;
